import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Loader2, Minus, Pencil, Plus, RotateCcw, X } from 'lucide-react'
import {
  ApiError,
  BURN_TYPES,
  CreatorRow,
  CreatorSlotsDashboard,
  fetchCreatorSlotsDashboard,
  fetchCreatorSlotsFilterOptions,
  previewDashboard,
  previewFilterOptions,
  recentMonths,
  saveCreatorSlotEdit,
  SLOT_TYPE_LABELS,
  SLOT_TYPES,
  SlotFilterOptions,
  SlotType,
  TypeRoll,
} from './creatorSlots'
import { MonthDropdown, SelectDropdown } from './FilterControls'

const MONTHS = recentMonths(12)
const nfmt = (n: number) => n.toLocaleString()
const GAUGE_MAX_WIDTH = 168 // 게이지 박스 줄바꿈 기준 폭(px) — 목표 크면 다음 줄로
const COL_COUNT = 2 + SLOT_TYPES.length + 3 // 등급+크리에이터 / 타입 / 인스타+소진율+편집

// 마감 슬롯을 주차 숫자 리스트로 (weekly[i] = i+1주 마감 수)
function weekList(roll: TypeRoll): number[] {
  const out: number[] = []
  roll.weekly.forEach((cnt, i) => {
    for (let k = 0; k < cnt; k++) out.push(i + 1)
  })
  return out
}

// 롱+숏 합산 소진율
function rowBurn(row: CreatorRow): { rate: number; allClosed: boolean; resolved: boolean } {
  let target = 0
  let closed = 0
  for (const t of BURN_TYPES) {
    const r = row.byType[t]
    if (!r || r.target <= 0) continue
    target += r.target
    closed += r.closed
  }
  if (target === 0) return { rate: 0, allClosed: false, resolved: false }
  return { rate: Math.min(closed, target) / target, allClosed: closed >= target, resolved: true }
}

// 내부 구분선은 전부 동일 굵기(hairline)로 통일 — 헤더/행/그룹헤더가 같은 선.
const th = 'whitespace-nowrap border-b border-line-hairline bg-surface-soft px-4 py-3 text-left lf-caption text-fg-3'
const td = 'whitespace-nowrap border-b border-line-hairline px-4 py-3 align-middle'

// 레이블 고정 순서: 데아드 > 케이프 > LBE > 레디너리 > 더넥레 > (그 외) > 미분류
const LABEL_RANK = new Map<string, number>([
  ['데아드', 1],
  ['daead', 1],
  ['caape', 2],
  ['caape!', 2],
  ['케이프', 2],
  ['lbe', 3],
  ['레디너리', 4],
  ['le.dinary', 4],
  ['ledinary', 4],
  ['더넥레', 5],
  ['the next label', 5],
  ['thenextlabel', 5],
])
const labelRank = (l: string) => (l === '' ? 999 : (LABEL_RANK.get(l.trim().toLowerCase()) ?? 900))

// 등급 규모 순위(구독자수) — 메가 > 탑 > 미들 > 마이크로
const TIER_RANK: Record<string, number> = { 메가: 4, 탑: 3, 미들: 2, 마이크로: 1 }
const tierRank = (t: string | null) => (t ? (TIER_RANK[t] ?? 0) : 0)
// 등급 배지 배경 농도(웜 잉크 스케일): 탑=블랙 · 메가=진회색 · 미들=회색 · 마이크로=연회색
const TIER_STYLE: Record<string, string> = {
  탑: 'bg-ink-600 text-fg-inverse',
  메가: 'bg-ink-800 text-fg-inverse',
  미들: 'bg-ink-400 text-fg-inverse',
  마이크로: 'bg-ink-200 text-fg-2',
}
function TierBadge({ tier }: { tier: string | null }) {
  if (!tier) return <span className="lf-caption text-fg-3">—</span>
  return (
    <span
      className={`inline-flex items-center rounded-r-pill px-2 py-[3px] lf-caption font-medium ${TIER_STYLE[tier] ?? 'bg-ink-200 text-fg-2'}`}
    >
      {tier}
    </span>
  )
}

// ── 목표 슬롯 게이지 (박스=목표 / 마감=잉크+주차숫자 / 빈=점선 + / 분수 마감/목표) ──
function SlotGauge({ roll }: { roll: TypeRoll | null }) {
  const target = roll?.target ?? 0
  const closed = roll?.closed ?? 0
  // 목표도 마감도 없으면(데이터 없음/미설정) 빈 칸
  if (!roll || (target <= 0 && closed <= 0)) {
    return <td className={td} />
  }
  const weeks = weekList(roll)
  // 박스 = 목표 개수. 마감이 목표를 넘으면 초과분 박스를 뒤에 더 그린다(넘치게).
  const boxCount = Math.max(target, weeks.length)
  return (
    <td className={td}>
      <div className="flex items-center gap-2.5">
        <span className="w-[40px] shrink-0 text-[14px] font-semibold lf-num">
          <span className="text-fg-1">{closed}</span>
          <span className="text-fg-disabled">/{target}</span>
        </span>
        <div className="flex flex-wrap items-center gap-1" style={{ width: GAUGE_MAX_WIDTH }}>
          {Array.from({ length: boxCount }).map((_, i) => {
            const w = weeks[i]
            const box =
              w != null ? (
                <span
                  title={`${w}주차 마감${i >= target ? ' · 목표 초과' : ''}`}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-r-1 bg-brand-pink-grey text-[11px] lf-num text-pink-700"
                >
                  {w}
                </span>
              ) : (
                <span
                  title="빈 구좌"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-r-1 border border-dashed border-line-soft text-fg-3"
                >
                  <Plus size={11} />
                </span>
              )
            return (
              <Fragment key={i}>
                {/* 목표 초과 경계선 — 목표 칸 뒤, 초과 박스가 있을 때만 */}
                {i === target && weeks.length > target && (
                  <span className="h-3 w-px shrink-0 self-center bg-line-soft" aria-hidden title="목표 경계" />
                )}
                {box}
              </Fragment>
            )
          })}
        </div>
      </div>
    </td>
  )
}

function MatrixSkeleton() {
  return (
    <div className="rounded-r-2 border border-line-soft p-4">
      <div className="space-y-2.5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="h-10 w-full animate-pulse rounded-r-1 bg-surface-muted" />
        ))}
      </div>
    </div>
  )
}

// ── 편집 모달 (저장 시 목표·주차 마감을 DB에 반영) ──
type Draft = Record<SlotType, { target: number; weekly: number[] } | null>

function toDraft(row: CreatorRow): Draft {
  const d = {} as Draft
  for (const t of SLOT_TYPES) {
    const r = row.byType[t]
    d[t] = r ? { target: r.target, weekly: [...r.weekly] } : null
  }
  return d
}

// 그 달의 ISO 주 수(목요일 개수 = 그 달에 귀속되는 ISO 주) — 4 또는 5
function isoWeeksInMonth(month: string): number {
  const y = Number(month.slice(0, 4))
  const m = Number(month.slice(5, 7))
  if (!y || !m) return 5
  const days = new Date(y, m, 0).getDate()
  let count = 0
  for (let d = 1; d <= days; d++) if (new Date(y, m - 1, d).getDay() === 4) count++
  return Math.min(5, count || 5)
}

function EditModal({
  row,
  month,
  onClose,
  onSave,
}: {
  row: CreatorRow
  month: string
  onClose: () => void
  onSave: (draft: Draft) => void
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(row))
  const firstRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const initialRef = useRef(JSON.stringify(toDraft(row)))
  const weekCount = isoWeeksInMonth(month)

  const dirty = JSON.stringify(draft) !== initialRef.current
  const closeRef = useRef<() => void>(() => onClose())
  closeRef.current = () => {
    if (dirty && !window.confirm('변경사항이 저장되지 않았습니다. 닫을까요?')) return
    onClose()
  }

  useEffect(() => {
    firstRef.current?.focus()
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden' // 배경 스크롤 잠금
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
        return
      }
      if (e.key === 'Tab') {
        // 포커스 트랩
        const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])',
        )
        if (!nodes || nodes.length === 0) return
        const list = Array.from(nodes)
        const first = list[0]
        const last = list[list.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [])

  const setTarget = (t: SlotType, v: number) =>
    setDraft((d) => ({ ...d, [t]: d[t] ? { ...d[t]!, target: Math.max(0, v) } : d[t] }))
  const setWeek = (t: SlotType, wi: number, v: number) =>
    setDraft((d) => {
      const cur = d[t]
      if (!cur) return d
      const weekly = [...cur.weekly]
      weekly[wi] = Math.max(0, v)
      return { ...d, [t]: { ...cur, weekly } }
    })

  const stepBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-r-1 border border-line-soft text-fg-2 hover:bg-surface-soft focus:shadow-focus focus:outline-none'
  const numCls =
    'rounded-r-1 border border-line-soft bg-surface-base text-center lf-body-sm lf-num text-fg-1 [appearance:textfield] focus:shadow-focus focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--bg-overlay, rgba(53,38,40,.45))' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${row.name} 구좌 편집`}
        className="flex max-h-[86vh] w-full max-w-[560px] flex-col rounded-r-2 border border-line-soft bg-surface-base shadow-lf-3"
      >
        {/* 헤더 (고정) */}
        <div className="flex items-start justify-between gap-3 border-b border-line-hairline px-5 py-3">
          <div>
            <div className="lf-h4 text-fg-1">{row.name}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 lf-caption text-fg-3">
              <span>{row.label || '—'}</span>
              {row.tier && (
                <>
                  <span aria-hidden>·</span>
                  <TierBadge tier={row.tier} />
                </>
              )}
              <span aria-hidden>·</span>
              <span className="lf-num">{month}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => closeRef.current()}
            aria-label="닫기"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-r-1 text-fg-3 hover:bg-surface-soft hover:text-fg-1 focus:shadow-focus focus:outline-none"
          >
            <X size={18} />
          </button>
        </div>

        {/* 안내 배너 (고정) */}
        <div className="px-5 pt-4">
          <div className="rounded-r-1 border border-line-soft bg-surface-soft px-3 py-2 lf-caption text-fg-3">
            목표치 수정은 <b className="font-semibold text-fg-2">{month}부터</b> 반영됩니다(과거 월 불변). 주차 마감은 편집한
            칸만 수동값으로 덮어쓰고, 나머지는 세일즈포스 집계를 유지합니다.
          </div>
        </div>

        {/* 타입 섹션 (스크롤) */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {SLOT_TYPES.map((t, ti) => {
            const cur = draft[t]
            if (!cur) {
              return (
                <div key={t} className="rounded-r-1 border border-line-hairline px-3 py-2 lf-body-sm text-fg-3">
                  {SLOT_TYPE_LABELS[t]} — 목표치 미설정
                </div>
              )
            }
            return (
              <div key={t} className="rounded-r-1 border border-line-soft px-3 py-3">
                <div className="mb-3 flex items-center justify-between">
                  <span className="lf-body-sm font-medium text-fg-1">{SLOT_TYPE_LABELS[t]}</span>
                  <div className="flex items-center gap-1">
                    <span className="lf-caption text-fg-3">목표 (월)</span>
                    <button
                      ref={ti === 0 ? firstRef : undefined}
                      type="button"
                      onClick={() => setTarget(t, cur.target - 1)}
                      aria-label={`${SLOT_TYPE_LABELS[t]} 목표 감소`}
                      className={stepBtn}
                    >
                      <Minus size={14} />
                    </button>
                    <input
                      type="number"
                      min={0}
                      value={cur.target}
                      onChange={(e) => setTarget(t, Number(e.target.value))}
                      aria-label={`${SLOT_TYPE_LABELS[t]} 목표치`}
                      className={`h-9 w-14 px-2 ${numCls}`}
                    />
                    <button
                      type="button"
                      onClick={() => setTarget(t, cur.target + 1)}
                      aria-label={`${SLOT_TYPE_LABELS[t]} 목표 증가`}
                      className={stepBtn}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  {cur.weekly.slice(0, weekCount).map((v, wi) => (
                    <label key={wi} className="flex flex-col items-center gap-1">
                      <span className="lf-caption text-fg-3">{wi + 1}주</span>
                      <input
                        type="number"
                        min={0}
                        value={v}
                        onChange={(e) => setWeek(t, wi, Number(e.target.value))}
                        aria-label={`${SLOT_TYPE_LABELS[t]} ${wi + 1}주 마감`}
                        className={`h-9 w-12 px-1 ${numCls}`}
                      />
                    </label>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        {/* 푸터 (고정) */}
        <div className="flex items-center justify-between gap-2 border-t border-line-hairline px-5 py-3">
          <button
            type="button"
            onClick={() => setDraft(toDraft(row))}
            title="수동 편집값을 버리고 원래(SF) 값으로 되돌립니다. 저장을 눌러야 반영됩니다."
            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-r-1 px-3 lf-body-sm text-fg-3 hover:text-fg-1 focus:shadow-focus focus:outline-none"
          >
            <RotateCcw size={14} /> 초기화 (SF 값으로)
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => closeRef.current()}
              className="min-h-[40px] rounded-r-1 border border-line-soft px-4 lf-body-sm font-medium text-fg-2 hover:bg-surface-soft focus:shadow-focus focus:outline-none"
            >
              취소
            </button>
            <button
              type="button"
              onClick={() => onSave(draft)}
              className="min-h-[40px] rounded-r-1 bg-brand-pink px-4 lf-body-sm font-medium text-brand-brown hover:opacity-90 focus:shadow-focus focus:outline-none"
            >
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function CreatorSlotsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const month = searchParams.get('month') || MONTHS[0]
  const label = searchParams.get('label') || ''
  const creatorId = searchParams.get('creatorId') || ''

  const patch = useCallback(
    (next: Record<string, string>) => {
      const sp = new URLSearchParams(searchParams)
      for (const [k, v] of Object.entries(next)) {
        if (v) sp.set(k, v)
        else sp.delete(k)
      }
      setSearchParams(sp, { replace: true })
    },
    [searchParams, setSearchParams],
  )
  const hasFilter = !!(label || creatorId)
  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams({ month }), { replace: true })
  }, [month, setSearchParams])

  const [data, setData] = useState<CreatorSlotsDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ message: string; status?: number } | null>(null)
  const [opts, setOpts] = useState<SlotFilterOptions>({ labels: [], creators: [] })
  const [editing, setEditing] = useState<CreatorRow | null>(null)
  const [toast, setToast] = useState<{ message: string; undo?: () => void } | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    fetchCreatorSlotsDashboard({ month, label: label || undefined, creatorId: creatorId || undefined })
      .then(setData)
      .catch((e) => {
        const err = e as ApiError
        // 로컬(dev)에서만 목 미리보기로 폴백. prod은 서버가 apps 토큰을 주입하므로
        // 401이 나면 그건 실제 오류 → 가짜 데이터 대신 정직하게 에러 표기.
        if (err.status === 401 && import.meta.env.DEV) {
          setData(previewDashboard(month, { label: label || undefined, creatorId: creatorId || undefined }))
        } else {
          setError({ message: err.message, status: err.status })
        }
      })
      .finally(() => setLoading(false))
  }, [month, label, creatorId])

  useEffect(() => load(), [load])

  const loadOptions = useCallback(() => {
    fetchCreatorSlotsFilterOptions()
      .then(setOpts)
      .catch(() => {
        if (import.meta.env.DEV) setOpts(previewFilterOptions())
      })
  }, [])
  useEffect(() => loadOptions(), [loadOptions])

  const creatorOptions = useMemo(
    () => (label ? opts.creators.filter((c) => c.label === label) : opts.creators),
    [label, opts],
  )
  const summary = data?.summary
  const rows = useMemo(() => {
    // 목표(capacity)가 하나라도 확정된 크리에이터만 — 엑셀에 없는(미시딩) 크리에이터 제외
    const list = (data?.rows ?? []).filter((r) => SLOT_TYPES.some((t) => r.byType[t]?.capacityResolved))
    return [...list].sort((a, b) => a.label.localeCompare(b.label, 'ko') || a.name.localeCompare(b.name, 'ko'))
  }, [data])
  // 레이블 그룹 + 그룹 내 규모(등급) 순 정렬. 미분류는 한 그룹('')으로 묶어 맨 아래.
  const groups = useMemo(() => {
    const UNCLASSIFIED = new Set(['미분류', '미정', '-', '—', 'null', 'undefined'])
    const norm = (l: string) => (!l || !l.trim() || UNCLASSIFIED.has(l.trim()) ? '' : l.trim())
    const m = new Map<string, CreatorRow[]>()
    for (const r of rows) {
      const key = norm(r.label)
      if (!m.has(key)) m.set(key, [])
      m.get(key)!.push(r)
    }
    for (const [, list] of m)
      list.sort((a, b) => tierRank(b.tier) - tierRank(a.tier) || a.name.localeCompare(b.name, 'ko'))
    return [...m.entries()].sort((a, b) => {
      const ra = labelRank(a[0])
      const rb = labelRank(b[0])
      if (ra !== rb) return ra - rb // 고정 순서, 미분류('')는 맨 아래
      return a[0].localeCompare(b[0], 'ko')
    })
  }, [rows])
  const preview = data?.preview ?? false
  const isEmpty = !loading && !error && data !== null && rows.length === 0
  const showSkeleton = loading && data === null
  // 필터/월 변경 재조회(기존 데이터 있음) — 스켈레톤 대신 살짝 흐리게 + 스피너
  const busy = loading && data !== null

  // 편집 저장 — 변경분을 DB에 PUT 후 refetch. 실패 시 모달 유지 + 에러 토스트.
  const applyDraft = useCallback(
    async (target: CreatorRow, draft: Draft) => {
      try {
        await saveCreatorSlotEdit({
          creatorId: target.creatorId,
          month,
          original: target.byType,
          draft,
        })
        setEditing(null)
        setToast({ message: `${target.name} 구좌를 저장했습니다.` })
        load()
      } catch (e) {
        setToast({ message: e instanceof ApiError ? `저장 실패: ${e.message}` : '저장에 실패했습니다.' })
      }
    },
    [month, load],
  )

  useEffect(() => {
    if (!toast) return
    const id = window.setTimeout(() => setToast(null), 6000)
    return () => window.clearTimeout(id)
  }, [toast])

  return (
    <div className="w-full px-6 py-6">
      <div className="mb-1 lf-h3 text-fg-1">구좌 현황 대시보드</div>
      <div className="mb-4 lf-body-sm text-fg-3">전속 크리에이터의 월 구좌 소진(마감·빈구좌)을 타입별로 확인합니다.</div>

      {preview && import.meta.env.DEV && (
        <div className="mb-4 rounded-r-2 border border-line-soft bg-surface-soft p-3 lf-body-sm text-fg-1">
          {/* 토큰 붙여넣기 패널은 제거한다(4ebfe8c, 은지) — vite 프록시가 .env 토큰을 주입하므로
              로컬에서 손으로 넣을 일이 없다. 미리보기 상태 고지만 남긴다. */}
          미리보기(로컬 dev) 데이터입니다. 실데이터는 apps/portal/.env 의 lecommend 토큰이 필요합니다.
        </div>
      )}

      {/* 필터바 */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <MonthDropdown value={month} onChange={(m) => patch({ month: m })} />
        <SelectDropdown
          label="레이블"
          placeholder="전체 레이블"
          value={label}
          options={opts.labels.map((l) => ({ value: l, label: l }))}
          onChange={(v) => patch({ label: v, creatorId: '' })}
        />
        <SelectDropdown
          label="크리에이터"
          placeholder="전체 크리에이터"
          value={creatorId}
          options={creatorOptions.map((o) => ({ value: o.id, label: o.name }))}
          onChange={(v) => patch({ creatorId: v })}
          searchable
        />
        {hasFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="ml-auto flex min-h-[36px] items-center gap-1 rounded-r-1 px-2 lf-body-sm text-fg-3 hover:text-fg-1 focus:shadow-focus focus:outline-none"
          >
            <RotateCcw size={14} /> 필터 초기화
          </button>
        )}
      </div>

      <div className="relative">
        {busy && (
          <div className="pointer-events-none absolute inset-x-0 top-1 z-10 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-r-1 border border-line-soft bg-surface-base px-3 py-1.5 shadow-lf-1 lf-caption text-fg-2">
              <Loader2 size={14} className="animate-spin" /> 불러오는 중…
            </span>
          </div>
        )}
        <div className={`transition-opacity duration-base ease-standard ${busy ? 'opacity-40' : ''}`}>
          {/* KPI */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiCard label="전체 구좌 (롱+숏)" value={summary ? nfmt(summary.total) : '–'} />
        <KpiCard label="마감" value={summary ? nfmt(summary.closed) : '–'} />
        <KpiCard label="잔여 (빈구좌)" value={summary ? nfmt(summary.remaining) : '–'} accent="warning" />
        <KpiCard
          label="소진율 (롱+숏)"
          value={summary ? (summary.total > 0 ? `${Math.round(summary.burnRate * 100)}%` : '–') : '–'}
        />
      </div>

      {error ? (
        <div className="flex flex-col items-center gap-3 rounded-r-2 border border-line-soft bg-surface-base py-12">
          <AlertTriangle className="text-status-warning" />
          <div className="lf-body-sm text-fg-2">{error.message}</div>
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1.5 rounded-r-1 bg-brand-blue px-4 py-2 lf-body-sm font-medium text-fg-inverse hover:opacity-90 focus:shadow-focus focus:outline-none"
          >
            <RotateCcw size={15} /> 다시 시도
          </button>
        </div>
      ) : showSkeleton ? (
        <MatrixSkeleton />
      ) : isEmpty ? (
        <div className="flex flex-col items-center gap-3 rounded-r-2 border border-line-soft bg-surface-base py-12">
          <div className="lf-body-sm text-fg-2">조건에 맞는 크리에이터가 없어요.</div>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-r-1 bg-brand-blue px-4 py-2 lf-body-sm font-medium text-fg-inverse hover:opacity-90 focus:shadow-focus focus:outline-none"
          >
            필터 초기화
          </button>
        </div>
      ) : (
        <div>
          <div className="w-full overflow-x-auto rounded-r-2 border border-line-soft">
            <table className="w-full border-separate border-spacing-0">
              <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-[1]">
                <tr>
                  <th className={th}>등급</th>
                  <th className={th}>크리에이터</th>
                  {SLOT_TYPES.map((t) => (
                    <th key={t} className={th}>
                      {SLOT_TYPE_LABELS[t]} 슬롯
                    </th>
                  ))}
                  <th className={`${th} text-center`}>인스타</th>
                  <th className={`${th} text-right`}>소진율</th>
                  <th className={`${th} text-center`}>편집</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(([groupLabel, list]) => (
                  <Fragment key={groupLabel}>
                    <tr>
                      <td
                        colSpan={COL_COUNT}
                        className="border-b border-line-hairline bg-surface-soft px-4 py-1.5 lf-eyebrow text-fg-2"
                      >
                        {groupLabel || '미분류'}
                      </td>
                    </tr>
                    {list.map((r) => {
                      const burn = rowBurn(r)
                      return (
                        <tr key={r.creatorId} className="hover:bg-surface-soft">
                          <td className={td}>
                            <TierBadge tier={r.tier} />
                          </td>
                          <td className={`${td} text-[14px] font-medium text-fg-1`}>{r.name}</td>
                          {SLOT_TYPES.map((t) => (
                            <SlotGauge key={t} roll={r.byType[t]} />
                          ))}
                          <td className={`${td} text-center`}>
                            <span className="inline-block rounded-r-pill border border-line-soft bg-surface-base px-2.5 py-1 lf-caption text-fg-3">
                              무한 · 상시
                            </span>
                          </td>
                          <td className={`${td} text-right`}>
                            {burn.resolved ? (
                              burn.allClosed ? (
                                <span className="lf-body-sm font-semibold text-fg-3">마감</span>
                              ) : (
                                <span className="lf-body-sm lf-num font-semibold text-status-warning">
                                  {Math.round(burn.rate * 100)}%
                                </span>
                              )
                            ) : null}
                          </td>
                          <td className={`${td} text-center`}>
                            <button
                              type="button"
                              onClick={() => setEditing(r)}
                              aria-label={`${r.name} 구좌 편집`}
                              title="목표치·주차 편집"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 hover:text-fg-1 focus:shadow-focus focus:outline-none"
                            >
                              <Pencil size={15} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* 범례 */}
          <div className="mt-2 flex flex-wrap items-center gap-3 lf-caption text-fg-3">
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-r-1 bg-brand-pink-grey text-[11px] text-pink-700">
                2
              </span>{' '}
              마감 (숫자=주차)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-r-1 border border-dashed border-line-strong">
                <Plus size={9} />
              </span>{' '}
              빈 구좌
            </span>
            <span>· 분수 = 마감/목표. 편집 버튼으로 목표치·주차 편집. 등급은 채널 구독자수 기준입니다.</span>
          </div>
        </div>
      )}
        </div>
      </div>

      {editing && (
        <EditModal row={editing} month={month} onClose={() => setEditing(null)} onSave={(d) => applyDraft(editing, d)} />
      )}

      {/* Undo 토스트 */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-3 rounded-r-1 bg-surface-ink px-4 py-2.5 shadow-lf-pop">
          <span className="lf-body-sm text-fg-inverse">{toast.message}</span>
          {toast.undo && (
            <button
              type="button"
              onClick={() => {
                toast.undo?.()
                setToast(null)
              }}
              className="lf-body-sm font-semibold text-fg-inverse underline underline-offset-2 hover:opacity-90 focus:shadow-focus focus:outline-none"
            >
              되돌리기
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function KpiCard({ label, value, accent }: { label: string; value: string; accent?: 'warning' }) {
  return (
    <div className="rounded-r-2 border border-line-soft bg-surface-base px-4 py-3">
      <div className="lf-eyebrow mb-3 text-fg-3">{label}</div>
      <div
        className={`font-display lf-num text-[30px] font-bold leading-none ${accent === 'warning' ? 'text-status-warning' : 'text-fg-1'}`}
      >
        {value}
      </div>
    </div>
  )
}
